import { BAND_ORDER, bandLabel } from 'libs/helpers/age-bands'

/**
 * Shared sizing helpers for PDP (`product-selector.js`) and Athos quick add.
 *
 * Age band *wording* is not owned here: it comes from the `size_age_bands` metaobject via
 * `libs/helpers/age-bands`, which the PDP size selector reads too, so a band caption on a
 * product card can never disagree with the same band on the product page. What stays here is
 * the inference the PDP does not need — deriving a band from an EU number or a "3-8 Y" label
 * when a variant carries no `age_group`, and aliasing the legacy Utopia values.
 *
 * Do not import `product-selector.js` from Athos; it registers `x-product-selector`.
 */

/** Display order, re-exported so Athos has one import for the band vocabulary. */
export const GLOBAL_HEADERS = BAND_ORDER

const AGE_GROUP_ALIASES = {
  Baby: 'Baby',
  Toddler: 'Toddler',
  Younger: 'Younger',
  '3-8 Yrs': 'Younger',
  Older: 'Older',
  '8+ Yrs': 'Older',
  Size: 'Sizes'
}

const SIZE_CATEGORY_KEYS = {
  BABY: 'Baby',
  TODDLER: 'Toddler',
  '3-8_YEARS': '3-8 Yrs',
  '8+_YEARS': '8+ Yrs'
}

export const sizeTypes = [
  { value: 'UK', label: 'UK' },
  { value: 'US', label: 'US' },
  { value: 'EURO', label: 'EU' },
  { value: 'CM', label: 'CM' }
]

export const DEFAULT_WIDTH_IF_PRESENT = 'E'
export const SORT_SIZE_CATEGORY_KEYS = Object.values(SIZE_CATEGORY_KEYS)

export const normaliseAgeGroup = (group) => AGE_GROUP_ALIASES[group] || group

/**
 * Customer-facing wording for one age band.
 *
 * `bandLabel` falls back to the value it was given, so an unmapped band - or an empty
 * metaobject - still renders the raw Utopia value rather than a blank heading.
 *
 * @param {string} group Raw `age_group` value, possibly a legacy alias.
 * @param {{short?: boolean}} [options] `short` picks the pill wording over the full label.
 * @returns {string}
 */
export const getAgeGroupLabel = (group, { short = false } = {}) => bandLabel(normaliseAgeGroup(group), { short })

export const sortAgeGroupCategories = (categories) =>
  [...categories].sort((a, b) => {
    const indexA = BAND_ORDER.indexOf(normaliseAgeGroup(a))
    const indexB = BAND_ORDER.indexOf(normaliseAgeGroup(b))
    return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB)
  })

export const getSizeGroupFromRange = (size) => {
  if (size < 20) {
    return SIZE_CATEGORY_KEYS.BABY
  }
  if (size >= 20 && size < 28) {
    return SIZE_CATEGORY_KEYS.TODDLER
  }
  if (size >= 28 && size < 34) {
    return SIZE_CATEGORY_KEYS['3-8_YEARS']
  }
  if (size >= 34) {
    return SIZE_CATEGORY_KEYS['8+_YEARS']
  }
}

export const ageGroupFromEuValue = (eu) => {
  const text = String(eu ?? '').trim()
  if (!/^\d+(?:\.\d+)?$/.test(text)) return 'Sizes'
  const euNumber = parseFloat(text)
  const ageGroup = !Number.isNaN(euNumber) ? getSizeGroupFromRange(euNumber) : ''
  return normaliseAgeGroup(ageGroup) || 'Sizes'
}

const ageGroupFromYearSizeLabel = (label) => {
  const match = String(label ?? '').trim().match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*Y$/i)
  if (!match) return ''
  const max = parseFloat(match[2])
  if (Number.isNaN(max)) return ''
  if (max <= 1) return ageGroupFromEuValue(17)
  if (max <= 3) return ageGroupFromEuValue(22)
  if (max <= 8) return ageGroupFromEuValue(30)
  return ageGroupFromEuValue(36)
}

export const resolveSizeAgeGroup = ({ ageGroup, eu, label } = {}) => {
  const fromMeta = normaliseAgeGroup(typeof ageGroup === 'string' ? ageGroup.trim() : '')
  if (fromMeta) return fromMeta
  const fromEu = ageGroupFromEuValue(eu)
  if (fromEu !== 'Sizes') return fromEu
  return ageGroupFromYearSizeLabel(label) || 'Sizes'
}

export const handleGetTypeValue = (type, size) => {
  switch (type) {
    case 'UK': {
      return size.uk
    }
    case 'US': {
      return size.us
    }
    case 'EURO': {
      return size.eu
    }
    case 'CM': {
      return size.cm
    }
  }
}

export const sizeGroups = (variants) => {
  const s = variants.reduce((acc, v) => {
    const group = v.age_group
    if (v?.width) {
      if (acc[group]) {
        if (acc[group][v.width]) {
          acc[group][v.width].push(v)
          return acc
        }
      }
      acc[group] = {
        ...acc[group],
        [v.width]: [v]
      }
      return acc
    }

    if (acc[group]) {
      acc[group].push(v)
      return acc
    }
    acc[group] = [v]
    return acc
  }, {})

  return s
}

export const parseSizeType = (c, type) => {
  return c.name.split('/').filter((t) => t.includes(type))
}

export const getSizeFromTitle = (row, type) => {
  const inputType = type === 'CM' ? 'EURO' : type
  const input = Array.isArray(row) ? row.pop() : row
  const name = parseSizeType(input, inputType)
  if (!name.length) {
    return '-'
  }
  const value = name.pop().trimEnd().split(' ').pop()
  if (type === 'CM') return (parseFloat(value) * 2.54).toFixed(2)
  return value
}

export const checkHeader = (parseSizeDataObjects) => {
  const keys = ['cm', 'us', 'eu', 'uk']
  const object = parseSizeDataObjects[0]
  if (!object) return []
  const arr = keys.reduce((acc, key) => {
    if (object[key] !== undefined) {
      acc.push(key)
    }
    return acc
  }, [])

  return arr
}

export const handleHasDefaultValue = (productsWidths, defaultValue) => {
  const hasDefault = productsWidths.widthOptions.find((option) => option.width === defaultValue)
  if (!hasDefault) return productsWidths

  if (!hasDefault.hasStock) return productsWidths

  const sortedWidthOptions = productsWidths.widthOptions.sort((a, b) => a.width.localeCompare(b.width))
  return {
    width: defaultValue,
    widthOptions: sortedWidthOptions
  }
}

export const handleDisplayStockOption = (data) => {
  const getWidthsAvailability = data.reduce((acc, d) => {
    if (!d?.width) {
      return acc
    }
    if (acc[d.width]) {
      acc[d.width].push(d)
      return acc
    }
    acc[d.width] = [d]
    return acc
  }, {})

  const widthOptions = Object.keys(getWidthsAvailability)

  if (!widthOptions.length) {
    return {
      width: '',
      widthOptions: []
    }
  }

  if (widthOptions.length === 1) {
    return {
      width: widthOptions[0],
      widthOptions: widthOptions.map((w) => ({ width: w, widthOptions: [{ width: w, hasStock: true }] }))
    }
  }

  const widthStockObjects = widthOptions.map((w) => {
    const hasStock = getWidthsAvailability[w].some((size) => size.available)
    return { width: w, hasStock }
  })

  const widthWithStock = widthStockObjects.find((s) => s.hasStock)
  if (!widthWithStock) {
    return { width: widthOptions[0], widthOptions: widthOptions.map((w) => ({ width: w, hasStock: true })) }
  }
  const sortedWidthOptions = widthStockObjects.sort((a, b) => b.hasStock - a.hasStock)

  return { width: widthWithStock.width, widthOptions: sortedWidthOptions }
}
